import React from 'react';
import { Button } from '@mui/material';
import writeXlsxFile from 'write-excel-file';
import { Node, Edge } from 'reactflow';
import { useSnackBar } from '../Common/SnackBar/index.tsx'; // Adjust path as needed

interface Props {
  nodes: Node[];
  edges: Edge[];
}

/**
 * Reusable function to export flow as Excel.
 */
export const handleDownloadExcelFile = async (
  nodes: Node[],
  edges: Edge[],
  showSnackBar: (msg: string, type: 'success' | 'error') => void
) => {
  const now = new Date().toISOString().split('T')[0];

  // Build connection maps
  const incomingMap: Record<string, string[]> = {};
  const outgoingMap: Record<string, string[]> = {};

  edges.forEach((edge) => {
    if (!incomingMap[edge.target]) incomingMap[edge.target] = [];
    if (!outgoingMap[edge.source]) outgoingMap[edge.source] = [];
    incomingMap[edge.target].push(edge.source);
    outgoingMap[edge.source].push(edge.target);
  });

  // Collect all unique keys from node data
  const allFields = new Set<string>();
  nodes.forEach((node) => {
    Object.keys(node.data || {}).forEach((key) => allFields.add(key));
  });

  const sortedFields = Array.from(allFields);

  // Header row
  const headerRow = [
    { value: 'Node ID' },
    { value: 'Type' },
    ...sortedFields.map((key) => ({ value: key })),
    { value: 'Position X' },
    { value: 'Position Y' },
    { value: 'Prev Connect' },
    { value: 'Next Connect' }
  ];

  // Data rows
  const dataRows = nodes.map((node) => {
    return [
      { value: node.id },
      { value: node.type },
      ...sortedFields.map((key) => {
        const val = node.data?.[key];
        if (typeof val === 'object') return { value: JSON.stringify(val) };
        return { value: val || '' };
      }),
      { value: node.position.x },
      { value: node.position.y },
      { value: (incomingMap[node.id] || []).join(', ') },
      { value: (outgoingMap[node.id] || []).join(', ') }
    ];
  });

  const rows = [headerRow, ...dataRows];

  try {
    await writeXlsxFile(rows, {
      fileName: `bot-flow-complete-${now}.xlsx`,
      sheet: 'Bot Flow'
    });
    showSnackBar('Bot flow downloaded as Excel!', 'success');
  } catch (error) {
    console.error('Excel download failed:', error);
    showSnackBar('Failed to download Excel.', 'error');
  }
};

/**
 * UI Button Component (optional use)
 */
const DownloadExcelButton: React.FC<Props> = ({ nodes, edges }) => {
  const { showSnackBar } = useSnackBar();

  return (
    <Button
      variant="contained"
      color="success"
      size="small"
      onClick={() => handleDownloadExcelFile(nodes, edges, showSnackBar)}
    >
      Download Excel
    </Button>
  );
};

export default DownloadExcelButton;
